import {
  faArrowTrendDown,
  faArrowTrendUp,
  faBell,
  faDoorClosed,
  faPenToSquare,
  faSparkles,
  faTag,
  faThumbsUp,
  faTriangleExclamation,
  faTrophy,
  faWandMagicSparkles,
} from '@fortawesome/pro-duotone-svg-icons'

import { IconDefinition } from '@fortawesome/fontawesome-svg-core'

import { ACHIEVEMENT_CATALOG } from '../../_defaults/achievement-catalog'
import { ImportedListing } from '../../_types/imported-listing'
import { QuickPassNotification } from '../../_types/quickpass-notification'
import { QuickPassProfile } from '../../_types/quickpass-profile'
import { ActivityEntry, ActivityKind } from './types'

const NOTIFICATION_KIND_MAP: Record<string, ActivityKind> = {
  'imported-listing-price-dropped': 'price-drop',
  'imported-listing-price-increased': 'price-increase',
  'imported-listing-became-available': 'became-available',
  'imported-listing-became-unavailable': 'became-unavailable',
  'imported-listing-scrape-failing': 'scrape-failing',
  'preference-match': 'system',
  system: 'system',
}

const NOTIFICATION_GLYPH_MAP: Record<ActivityKind, IconDefinition> = {
  'price-drop': faArrowTrendDown,
  'price-increase': faArrowTrendUp,
  'became-available': faSparkles,
  'became-unavailable': faDoorClosed,
  'scrape-failing': faTriangleExclamation,
  'listing-imported': faTag,
  'achievement-unlocked': faTrophy,
  'tag-set': faThumbsUp,
  'note-added': faPenToSquare,
  system: faBell,
}

/**
 * Builds activity entries from the renter's saved-notifications. Each
 * notification becomes one entry mapped to a kind so the feed can use
 * its own glyph palette.
 *
 * @param {QuickPassNotification[]} notifications - the renter's notifications
 * @returns {ActivityEntry[]} the resulting activity entries
 */
const entriesFromNotifications = (
  notifications: QuickPassNotification[],
): ActivityEntry[] => {
  return notifications.map(notification => {
    const kind = NOTIFICATION_KIND_MAP[notification.type] ?? 'system'

    return {
      id: `notification-${notification.id}`,
      kind,
      title: notification.title,
      body: notification.body,
      occurredAt: notification.createdAt,
      glyph: NOTIFICATION_GLYPH_MAP[kind],
      linkPath: notification.linkPath === '' ? null : notification.linkPath,
    }
  })
}

/**
 * Builds an activity entry for each imported listing's createdAt — so
 * the renter sees their own imports in the timeline. Skips listings
 * that have been archived since the entry would link to nothing.
 *
 * @param {ImportedListing[]} listings - the renter's listings
 * @param {Set<string>} archivedIds - set of archived listing ids
 * @returns {ActivityEntry[]} the resulting activity entries
 */
const entriesFromImports = (
  listings: ImportedListing[],
  archivedIds: Set<string>,
): ActivityEntry[] => {
  return listings
    .filter(listing => archivedIds.has(listing.id) === false)
    .map(listing => ({
      id: `import-${listing.id}`,
      kind: 'listing-imported' as ActivityKind,
      title: 'Listing imported',
      body: `You added ${listing.title} from ${listing.sourceSite}.`,
      occurredAt: listing.createdAt,
      glyph: NOTIFICATION_GLYPH_MAP['listing-imported'],
      linkPath: `/imported-listings/${listing.id}`,
    }))
}

/**
 * Builds an activity entry for the renter's onboarding completion if
 * one has happened. Lets the timeline anchor at "I joined QuickPass".
 *
 * @param {QuickPassProfile | null} profile - the renter profile
 * @returns {ActivityEntry[]} an array of zero or one entries
 */
const entriesFromOnboarding = (
  profile: QuickPassProfile | null,
): ActivityEntry[] => {
  if (profile === null || profile.onboardingCompletedAt === null) {
    return []
  }

  return [
    {
      id: 'onboarding-complete',
      kind: 'system',
      title: 'Welcome to QuickPass',
      body: 'You finished onboarding. The watching starts here.',
      occurredAt: profile.onboardingCompletedAt,
      glyph: faWandMagicSparkles,
      linkPath: '/dashboard',
    },
  ]
}

/**
 * Builds activity entries from the renter's unlocked achievements.
 * createdAt isn't tracked per achievement (we only store the id list);
 * the entries are stamped with the profile's updatedAt so they sort
 * roughly with the rest of the feed.
 *
 * @param {QuickPassProfile | null} profile - the renter profile
 * @returns {ActivityEntry[]} the resulting activity entries
 */
const entriesFromAchievements = (
  profile: QuickPassProfile | null,
): ActivityEntry[] => {
  if (profile === null) {
    return []
  }

  const unlocked = profile.unlockedAchievementIds ?? []
  const baseStamp = profile.updatedAt > 0 ? profile.updatedAt : Date.now()

  return unlocked
    .map((achievementId, index): ActivityEntry | null => {
      const descriptor =
        ACHIEVEMENT_CATALOG[achievementId as keyof typeof ACHIEVEMENT_CATALOG]
      if (descriptor === undefined) {
        return null
      }

      return {
        id: `achievement-${achievementId}`,
        kind: 'achievement-unlocked' as ActivityKind,
        title: `Unlocked: ${descriptor.title}`,
        body: descriptor.unlockBlurb,
        occurredAt: baseStamp - index,
        glyph: descriptor.glyph,
        linkPath: '/dashboard',
      }
    })
    .filter((entry): entry is ActivityEntry => entry !== null)
}

/**
 * Combines every activity source into a single chronologically sorted
 * timeline. Newest first.
 *
 * @param {QuickPassNotification[]} notifications - the renter's notifications
 * @param {ImportedListing[]} listings - the renter's listings
 * @param {QuickPassProfile | null} profile - the renter profile
 * @returns {ActivityEntry[]} the merged + sorted feed
 */
const buildActivityFeed = (
  notifications: QuickPassNotification[],
  listings: ImportedListing[],
  profile: QuickPassProfile | null,
): ActivityEntry[] => {
  const archivedIds = new Set(profile?.archivedListingIds ?? [])

  const merged = [
    ...entriesFromNotifications(notifications),
    ...entriesFromImports(listings, archivedIds),
    ...entriesFromOnboarding(profile),
    ...entriesFromAchievements(profile),
  ]

  merged.sort((alpha, beta) => beta.occurredAt - alpha.occurredAt)
  return merged
}

export { buildActivityFeed }
